import type { PlaceholderDefinition } from 'types';

export const SERVICE_AGREEMENT_TYPE = 'service-agreement';
export const COMPANY_NAME_SAMPLE = 'ABC Immigration Services';
export const COMPANY_TYPED_SIGNATURE_FONT = 'Allura';
export const COMPANY_TYPED_SIGNATURE_COLOR = '#1e40af';
export const COMPANY_TYPED_SIGNATURE_SIZE = '32px';

const AGENT_NAME_SIGNATURE_TOKENS = new Set(['{{agent.name}}', '{{agent.signature}}']);
const COMPANY_NAME_SIGNATURE_TOKENS = new Set(['{{company.name}}', '{{company.signature}}']);

/** Typed company signature: company name in the default script font, in blue. */
export const renderTypedCompanySignature = (name: string): string =>
    `<span style="font-family: ${COMPANY_TYPED_SIGNATURE_FONT}, cursive; font-size: ${COMPANY_TYPED_SIGNATURE_SIZE}; color: ${COMPANY_TYPED_SIGNATURE_COLOR};">${name}</span>`;

// Mandatory placeholders that must be present in every template
export const MANDATORY_PLACEHOLDERS: PlaceholderDefinition[] = [
    {
        token: '{{client.name}}',
        label: 'Client Name',
        category: 'client',
        mandatory: true,
        sampleValue: 'John Doe',
        description: 'Full name of the client',
    },
    {
        token: '{{client.email}}',
        label: 'Client Email',
        category: 'client',
        mandatory: true,
        sampleValue: 'john.doe@example.com',
        description: 'Email address of the client',
    },
    {
        token: '{{program.name}}',
        label: 'Program Name',
        category: 'program',
        mandatory: true,
        sampleValue: 'Express Entry',
        description: 'Name of the immigration program',
    },
    {
        token: '{{program.country}}',
        label: 'Program Country',
        category: 'program',
        mandatory: true,
        sampleValue: 'Canada',
        description: 'Country of the immigration program',
    },
    {
        token: '{{client.signature}}',
        label: 'Client Signature',
        category: 'client',
        mandatory: true,
        sampleValue: '______________________',
        description: "Client's signature space – required for all retainer agreements",
    },
    {
        token: '{{agent.signature}}',
        label: 'Agent Signature',
        category: 'agent',
        mandatory: true,
        sampleValue:
            '<span style="font-family: Allura, cursive; font-size: 32px; color: #000000;">John Smith</span>',
        description: 'Digital signature of the assigned immigration consultant',
    },
    {
        token: '{{agent.name}}',
        label: 'Agent Name',
        category: 'agent',
        mandatory: true,
        sampleValue: 'John Smith',
        description: 'Full name of the assigned immigration consultant',
    },
];

// Optional placeholders
export const OPTIONAL_PLACEHOLDERS: PlaceholderDefinition[] = [
    {
        token: '{{client.phone}}',
        label: 'Client Phone',
        category: 'client',
        mandatory: false,
        sampleValue: '+1 (555) 123-4567',
        description: 'Phone number of the client',
    },
    {
        token: '{{client.address}}',
        label: 'Client Address',
        category: 'client',
        mandatory: false,
        sampleValue: '123 Main St, Toronto, ON',
        description: 'Address of the client',
    },
    {
        token: '{{program.type}}',
        label: 'Program Type',
        category: 'program',
        mandatory: false,
        sampleValue: 'Federal Skilled Worker',
        description: 'Type of immigration program',
    },
    {
        token: '{{retainer.total_fee}}',
        label: 'Total Fee',
        category: 'retainer',
        mandatory: false,
        sampleValue: '$5,000.00',
        description: 'Total retainer fee amount',
    },
    {
        token: '{{retainer.currency}}',
        label: 'Currency',
        category: 'retainer',
        mandatory: false,
        sampleValue: 'CAD',
        description: 'Currency code',
    },
    {
        token: '{{retainer.milestones_table}}',
        label: 'Milestones Table',
        category: 'retainer',
        mandatory: false,
        sampleValue: '[Milestone table will be inserted here]',
        description: 'Table showing payment milestones',
    },
    {
        token: '{{date.today}}',
        label: "Today's Date",
        category: 'system',
        mandatory: false,
        sampleValue: new Date().toLocaleDateString(),
        description: 'Current date',
    },
    {
        token: '{{date.signing}}',
        label: 'Signing Date',
        category: 'system',
        mandatory: false,
        sampleValue: new Date().toLocaleDateString(),
        description: 'Date when the retainer is signed',
    },
    {
        token: '{{page.number}}',
        label: 'Current Page Number',
        category: 'system',
        mandatory: false,
        sampleValue: '1',
        description: 'Current page number (auto-generated in PDF)',
    },
    {
        token: '{{page.total}}',
        label: 'Total Pages',
        category: 'system',
        mandatory: false,
        sampleValue: '10',
        description: 'Total number of pages (auto-generated in PDF)',
    },
    // Agent placeholders
    {
        token: '{{agent.title}}',
        label: 'Agent Title',
        category: 'agent',
        mandatory: false,
        sampleValue: 'RCIC, Immigration Consultant',
        description: 'Professional title and credentials',
    },
    {
        token: '{{agent.email}}',
        label: 'Agent Email',
        category: 'agent',
        mandatory: false,
        sampleValue: 'john.smith@immigrationfirm.com',
        description: 'Contact email of the agent',
    },
    {
        token: '{{agent.phone}}',
        label: 'Agent Phone',
        category: 'agent',
        mandatory: false,
        sampleValue: '+1 (416) 555-0123',
        description: 'Contact phone number of the agent',
    },
    {
        token: '{{agent.registration_number}}',
        label: 'Agent Registration Number',
        category: 'agent',
        mandatory: false,
        sampleValue: 'R123456',
        description: 'Registration number of the visa agent assigned to the client',
    },
    {
        token: '{{agent.issue_date}}',
        label: 'License Issue Date',
        category: 'agent',
        mandatory: false,
        sampleValue: 'March 01, 2020',
        description: 'Issue date of the assigned visa agent’s license',
    },
    // Company placeholders
    {
        token: '{{company.name}}',
        label: 'Company Name',
        category: 'company',
        mandatory: false,
        sampleValue: COMPANY_NAME_SAMPLE,
        description: 'Name of the immigration service provider firm',
    },
    {
        token: '{{company.email}}',
        label: 'Company Email',
        category: 'company',
        mandatory: false,
        sampleValue: 'info@immigrationfirm.com',
        description: 'General contact email for the company',
    },
    {
        token: '{{company.phone}}',
        label: 'Company Phone',
        category: 'company',
        mandatory: false,
        sampleValue: '+1 (416) 555-0100',
        description: 'Main contact phone for the company',
    },
    {
        token: '{{company.signature}}',
        label: 'Company Signature',
        category: 'company',
        mandatory: false,
        sampleValue: renderTypedCompanySignature(COMPANY_NAME_SAMPLE),
        description: 'Company name rendered as a typed signature',
    },
];

// All placeholders combined
export const PLACEHOLDERS: PlaceholderDefinition[] = [
    ...MANDATORY_PLACEHOLDERS,
    ...OPTIONAL_PLACEHOLDERS,
];

// Placeholders organized by category
const placeholdersInCategory = (category: string): PlaceholderDefinition[] =>
    PLACEHOLDERS.filter((placeholder) => placeholder.category === category);

export const PLACEHOLDERS_BY_CATEGORY: Record<string, PlaceholderDefinition[]> = {
    client: placeholdersInCategory('client'),
    program: placeholdersInCategory('program'),
    retainer: placeholdersInCategory('retainer'),
    system: placeholdersInCategory('system'),
    agent: placeholdersInCategory('agent'),
    company: placeholdersInCategory('company'),
};

export const AGENT_REGISTRATION_NUMBER_TOKEN = '{{agent.registration_number}}';
export const AGENT_LICENSE_ISSUE_DATE_TOKEN = '{{agent.issue_date}}';

// Category labels for display
export const CATEGORY_LABELS: Record<string, string> = {
    client: 'Client Information',
    program: 'Program Information',
    retainer: 'Retainer & Pricing',
    system: 'System & Dates',
    agent: 'Agent Information',
    company: 'Company Information',
};

// ─── B2B Org-to-Org Template Placeholders ───────────────────────────────────

export const B2B_MANDATORY_PLACEHOLDERS: PlaceholderDefinition[] = [
    {
        token: '{{party_a.org_name}}',
        label: 'Provider Org Name',
        category: 'party_a',
        mandatory: true,
        sampleValue: 'ABC Immigration Services',
        description: 'Legal name of your organization (Party A)',
    },
    {
        token: '{{party_a.signatory_name}}',
        label: 'Provider Signatory',
        category: 'party_a',
        mandatory: true,
        sampleValue: 'Jane Smith',
        description: 'Name of the person signing on behalf of your organization',
    },
    {
        token: '{{party_a.signatory_title}}',
        label: 'Provider Title',
        category: 'party_a',
        mandatory: true,
        sampleValue: 'Director',
        description: 'Title/role of your signatory',
    },
    {
        token: '{{party_a.signature}}',
        label: 'Provider Signature',
        category: 'party_a',
        mandatory: true,
        sampleValue: '______________________',
        description: 'Signature block for your organization',
    },
    {
        token: '{{party_b.org_name}}',
        label: 'Partner Org Name',
        category: 'party_b',
        mandatory: true,
        sampleValue: 'XYZ Consultants Ltd',
        description: 'Legal name of the partner organization (Party B)',
    },
    {
        token: '{{party_b.signatory_name}}',
        label: 'Partner Signatory',
        category: 'party_b',
        mandatory: true,
        sampleValue: 'John Doe',
        description: 'Name of the person signing on behalf of the partner',
    },
    {
        token: '{{party_b.signatory_title}}',
        label: 'Partner Title',
        category: 'party_b',
        mandatory: true,
        sampleValue: 'CEO',
        description: 'Title/role of the partner signatory',
    },
    {
        token: '{{party_b.signature}}',
        label: 'Partner Signature',
        category: 'party_b',
        mandatory: true,
        sampleValue: '______________________',
        description: 'Signature block for the partner organization',
    },
];

export const B2B_OPTIONAL_PLACEHOLDERS: PlaceholderDefinition[] = [
    {
        token: '{{party_a.signed_date}}',
        label: 'Provider Signed Date',
        category: 'party_a',
        mandatory: false,
        sampleValue: 'January 01, 2026',
        description: 'Date your organization signed',
    },
    {
        token: '{{party_b.signed_date}}',
        label: 'Partner Signed Date',
        category: 'party_b',
        mandatory: false,
        sampleValue: 'January 15, 2026',
        description: 'Date the partner signed',
    },
    {
        token: '{{effective_date}}',
        label: 'Effective Date',
        category: 'contract',
        mandatory: false,
        sampleValue: 'January 01, 2026',
        description: 'Date the agreement comes into effect',
    },
    {
        token: '{{expiry_date}}',
        label: 'Expiry Date',
        category: 'contract',
        mandatory: false,
        sampleValue: 'December 31, 2026',
        description: 'Date the agreement expires',
    },
    {
        token: '{{date.today}}',
        label: "Today's Date",
        category: 'contract',
        mandatory: false,
        sampleValue: 'April 07, 2026',
        description: 'Current date',
    },
];

export const B2B_PLACEHOLDERS: PlaceholderDefinition[] = [
    ...B2B_MANDATORY_PLACEHOLDERS,
    ...B2B_OPTIONAL_PLACEHOLDERS,
];

export const B2B_MANDATORY_PLACEHOLDER_TOKENS = new Set(
    B2B_MANDATORY_PLACEHOLDERS.map((p) => p.token)
);

export const B2B_PLACEHOLDERS_BY_CATEGORY: Record<string, PlaceholderDefinition[]> = {
    party_a: B2B_PLACEHOLDERS.filter((p) => p.category === 'party_a'),
    party_b: B2B_PLACEHOLDERS.filter((p) => p.category === 'party_b'),
    contract: B2B_PLACEHOLDERS.filter((p) => p.category === 'contract'),
};

export const B2B_CATEGORY_LABELS: Record<string, string> = {
    party_a: 'Provider (Your Organization)',
    party_b: 'Partner (Counter-party)',
    contract: 'Contract Dates',
};

const B2B_TEMPLATE_TYPES = new Set([
    'referral',
    'subcontractor',
    'msa',
    'revenue-sharing',
    'nda',
    'preferred-partner',
]);

const applyServiceAgreementPlaceholderRules = (
    placeholders: PlaceholderDefinition[]
): PlaceholderDefinition[] =>
    placeholders.map((placeholder) => {
        if (AGENT_NAME_SIGNATURE_TOKENS.has(placeholder.token)) {
            return { ...placeholder, mandatory: false };
        }
        if (COMPANY_NAME_SIGNATURE_TOKENS.has(placeholder.token)) {
            return { ...placeholder, mandatory: true };
        }
        return placeholder;
    });

/**
 * Returns the correct placeholder set based on the template type.
 * B2B template types use org-to-org placeholders; service agreements require
 * company name/signature instead of agent name/signature; others use the
 * individual client placeholders.
 */
export const getPlaceholdersForType = (templateType?: string): PlaceholderDefinition[] => {
    if (templateType && B2B_TEMPLATE_TYPES.has(templateType)) {
        return B2B_PLACEHOLDERS;
    }
    if (templateType === SERVICE_AGREEMENT_TYPE) {
        return applyServiceAgreementPlaceholderRules(PLACEHOLDERS);
    }
    return PLACEHOLDERS;
};

export const getMandatoryPlaceholdersForType = (templateType?: string): PlaceholderDefinition[] => {
    if (templateType && B2B_TEMPLATE_TYPES.has(templateType)) {
        return B2B_MANDATORY_PLACEHOLDERS;
    }
    if (templateType === SERVICE_AGREEMENT_TYPE) {
        return getPlaceholdersForType(templateType).filter((placeholder) => placeholder.mandatory);
    }
    return MANDATORY_PLACEHOLDERS;
};

export const getCategoryLabelsForType = (templateType?: string): Record<string, string> =>
    templateType && B2B_TEMPLATE_TYPES.has(templateType) ? B2B_CATEGORY_LABELS : CATEGORY_LABELS;

export const getPlaceholdersByCategoryForType = (
    templateType?: string
): Record<string, PlaceholderDefinition[]> => {
    if (templateType && B2B_TEMPLATE_TYPES.has(templateType)) {
        return B2B_PLACEHOLDERS_BY_CATEGORY;
    }
    if (templateType === SERVICE_AGREEMENT_TYPE) {
        const placeholders = getPlaceholdersForType(templateType);
        return Object.fromEntries(
            Object.keys(PLACEHOLDERS_BY_CATEGORY).map((category) => [
                category,
                placeholders.filter((placeholder) => placeholder.category === category),
            ])
        );
    }
    return PLACEHOLDERS_BY_CATEGORY;
};
